import { OwnedTargetInfo, Port, ViewportData } from 'ais-facade-api/api';
import { svgToDataURL } from 'app/helpers';
import { getAircraftIcon, getPortIcon, getTargetSelectedFrame, getTerminalIcon, getVesselIcon } from 'helpers/getWebGLPointIcon';
import { useAppSelector } from 'app/store/store';
import { IconLayer } from '@deck.gl/layers/typed';
import { GeoJsonLayer } from 'deck.gl/typed';
import { useEffect, useMemo, useState } from 'react';
import { useLazyGetPortCardQuery } from '../../../../api/facade';
import { parseSync } from '@loaders.gl/core';
import { WKTLoader } from '@loaders.gl/wkt';
import { motionlessSpeed } from 'app/constants/_constants';

interface PortGeometryI {
  type: string;
  coordinates: number[] | number[][][] | number[][][][];
}

const handleGetPosition = (d: ViewportData | OwnedTargetInfo | Port): [number, number, number] => {
  if (!d) return [999, 999, 0];
  if ('lon' in d && 'lat' in d) {
    if (typeof d?.lon === 'number' && typeof d?.lat === 'number') return [d.lon, d.lat, 0];
  }
  if ('position' in d) {
    if (typeof d?.position?.longitude === 'number' && typeof d?.position?.latitude === 'number')
      return [d.position.longitude, d.position.latitude, 0];
  }
  return [999, 999, 0];
};

const handleGetAngle = (d: ViewportData | OwnedTargetInfo | Port) => {
  if (!d) return 0;
  const speed = 'sog' in d ? d.sog : 'position' in d ? d.position?.sog : null;
  if (typeof speed === 'number' && speed < motionlessSpeed) return 0;

  const isAton =
    ('registrySpecialType' in d && d.registrySpecialType === 'ATON') ||
    ('registryData' in d && d.registryData?.specialType === 'ATON');
  if (isAton) {
    return 0;
  }
  if ('position' in d) {
    return 360 - (d.position?.heading || d.position?.cog || 0);
  }
  if ('heading' in d || 'cog' in d) {
    return 360 - (d?.heading || d?.cog || 0);
  } else return 0;
};

export const useLayerTargetSelected = (showIndicator?: boolean) => {
  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const isAnalyticsMode = mode === 'analytics';
  const isHistoryOrPlaybackMode = mode === 'history' || mode === 'playback';

  const isVesselPlaybackActive = useAppSelector((state) => state.mapStateSlice.isVesselPlaybackActive);

  const selectedTarget = useAppSelector((state) => state.selectedTargetsSlice?.selectedTarget);
  const isLayerVisible = selectedTarget?.target?.object && Object.keys(selectedTarget?.target.object).length !== 0;
  const portIsSelected = selectedTarget?.type === 'port';
  const portKey = portIsSelected && selectedTarget?.target?.object?.key;
  const [portDataGeometry, setPortDataGeometry] = useState<PortGeometryI | null>(null);
  const [getPortCardData] = useLazyGetPortCardQuery();

  useEffect(() => {
    setPortDataGeometry(null);
  }, [selectedTarget]);

  useEffect(() => {
    if (getPortCardData && portKey) {
      getPortCardData(portKey).then((response) => {
        if (response?.data?.result?.geometry) {
          const newGeometry: string = response?.data?.result?.geometry;
          const coordinates = parseSync(newGeometry, WKTLoader);
          setPortDataGeometry(coordinates);
        }
      });
    }
  }, [portKey, getPortCardData]);

  const getTargetIcon = (d: any) => {
    let icon = '';
    const special = window._env_?.SPECIAL ? JSON.parse(window._env_.SPECIAL) : null;

    switch (selectedTarget?.type) {
      case 'vessel':
      case 'aton':
        icon =
          special !== null && special.includes(d.mmsi)
            ? getAircraftIcon()
            : getVesselIcon(d, showIndicator, isHistoryOrPlaybackMode);
        break;
      case 'port':
        icon = getPortIcon();
        break;
      case 'terminal':
        icon = getTerminalIcon();
        break;
      default:
        break;
    }
    return svgToDataURL(icon);
  };

  const layerTargetSelectedFrame = new IconLayer({
    id: 'icon-layer-selected-target-frame',
    data: [selectedTarget?.target?.object],
    pickable: true,
    visible: isLayerVisible && !isAnalyticsMode && !isVesselPlaybackActive,

    getPosition: handleGetPosition,
    getIcon: (d) => ({
      url: svgToDataURL(getTargetSelectedFrame(d)),
      width: 32,
      height: 32,
    }),
    updateTriggers: {
      getIcon: selectedTarget?.target?.object ? selectedTarget.target.object : null,
    },

    sizeMinPixels: 32,
  });

  const layerTargetSelected = new IconLayer({
    id: 'icon-layer-selected-target',
    data: [selectedTarget?.target?.object],
    pickable: true,
    visible: isLayerVisible && !isAnalyticsMode && !isVesselPlaybackActive,
    getPosition: handleGetPosition,
    getIcon: (d) => ({
      url: getTargetIcon(d),
      width: selectedTarget?.type === 'vessel' ? 28 : 32,
      height: selectedTarget?.type === 'vessel' ? 28 : 32,
    }),
    getAngle: handleGetAngle,
    updateTriggers: {
      getIcon: selectedTarget?.target?.object ? selectedTarget.target.object : null,
    },
    sizeMinPixels: selectedTarget?.type === 'vessel' ? 28 : 32,
  });

  const layerSelectedPort = useMemo(() => {
    if (portIsSelected && portDataGeometry !== null) {
      const portLayerArea = new GeoJsonLayer({
        id: `${'polygon-layer-port'}`,
        data: portDataGeometry,
        filled: true,
        stroked: true,
        getFillColor: [0, 0, 0, 0],
        getLineColor: [239, 16, 16, 255],
        getLineWidth: 5,
        lineWidthUnits: 'pixels',
        visible: portIsSelected,
      });
      const portLayerBorder = new GeoJsonLayer({
        id: 'polygon-border-port-layer',
        data: portDataGeometry,
        filled: false,
        stroked: true,
        getLineColor: [255, 255, 255, 190],
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        visible: portIsSelected,
      });
      return [portLayerArea, portLayerBorder];
    }
  }, [portIsSelected, portDataGeometry]);

  return [layerSelectedPort, layerTargetSelected, layerTargetSelectedFrame];
};
